import React, { Component, ErrorInfo, ReactNode } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Paper,
  Container,
} from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  Logout,
} from '@mui/icons-material';
import logger from '@/core/services';
import { AuthProvider } from '@refinedev/core';

interface Props extends WithTranslation {
  children: ReactNode;
  authProvider?: AuthProvider;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  errorInfo?: ErrorInfo;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error('Error caught by ErrorBoundary:', error, errorInfo);
    this.setState({ errorInfo });
  }

  private handleLogout = (): void => {
    this.props.authProvider?.logout?.({});
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleCopyError = (): void => {
    const errorDetails = {
      message: this.state.error?.message || 'Unknown error',
      name: this.state.error?.name,
      stack: this.state.error?.stack,
      componentStack: this.state.errorInfo?.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    navigator.clipboard.writeText(JSON.stringify(errorDetails, null, 2));
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <Container maxWidth="sm">
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '50vh',
                py: 4,
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  width: '100%',
                  textAlign: 'center',
                  borderRadius: 2,
                }}
              >
                <ErrorIcon
                  sx={{
                    fontSize: 64,
                    color: 'error.main',
                    mb: 2,
                  }}
                />

                <Typography
                  variant="h5"
                  component="h1"
                  gutterBottom
                  color="text.primary"
                  sx={{ fontWeight: 'medium' }}
                >
                  {this.props.t('errors.somethingWentWrong')}
                </Typography>

                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {this.props.t('errors.renderingErrorDescription')}
                </Typography>

                <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                  <AlertTitle>
                    {this.props.t('errors.renderingError')}
                  </AlertTitle>
                  {this.state.error?.message && (
                    <Typography variant="body2" component="div">
                      {this.state.error.message}
                    </Typography>
                  )}
                </Alert>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    startIcon={<CopyIcon />}
                    onClick={this.handleCopyError}
                    sx={{ minWidth: 120 }}
                  >
                    {this.props.t('buttons.copyError')}
                  </Button>

                  {this.props.authProvider && (
                    <Button
                      variant="contained"
                      startIcon={<Logout />}
                      onClick={this.handleLogout}
                      sx={{ minWidth: 120 }}
                    >
                      {this.props.t('buttons.logout')}
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={this.handleReload}
                    sx={{ minWidth: 120 }}
                  >
                    {this.props.t('buttons.retry')}
                  </Button>
                </Box>
              </Paper>
            </Box>
          </Container>
        )
      );
    }

    return this.props.children;
  }
}

const TranslatedErrorBoundary = withTranslation()(ErrorBoundary);
export default TranslatedErrorBoundary;
